import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faXmark, faRotateRight, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { myDeskService } from '../../services/myDeskService';
import { getWidgetBody } from './widgets';
import { prefersReducedMotion } from './useFlip';
import { snapSpan } from './layoutMath';

const SIZES = [
  { value: 'third', label: '1', title: 'One column' },
  { value: 'two_thirds', label: '2', title: 'Two columns' },
  { value: 'full', label: '3', title: 'Full width' },
];

/**
 * Loads one widget instance's data from the server and refreshes it on the
 * interval the catalog specifies. The server resolves the widget by id from
 * the stored layout and re-checks access on every call.
 */
const useWidgetData = (widgetId, refreshSeconds) => {
  const [state, setState] = useState({ data: null, loading: true, error: null, fetchedAt: null });
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const load = useCallback(async (silent = false, attempt = 0) => {
    if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await myDeskService.getWidgetData(widgetId);
      if (!mountedRef.current) return;
      setState({ data: result?.data ?? null, loading: false, error: null, fetchedAt: result?.fetched_at || null });
    } catch (err) {
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      // A widget added a moment ago may not be in the stored layout yet
      // when its first data call lands; give the save one chance to finish.
      if (status === 404 && attempt === 0) {
        setTimeout(() => { if (mountedRef.current) load(silent, 1); }, 1200);
        return;
      }
      const message = status === 403
        ? 'You no longer have access to this widget.'
        : status === 404
          ? 'This widget is no longer in your workspace.'
          : 'Could not load right now.';
      console.error(`My Desk: widget ${widgetId} failed to load`, err);
      setState((s) => ({ ...s, loading: false, error: message }));
    }
  }, [widgetId]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    if (refreshSeconds > 0) {
      timerRef.current = setInterval(() => load(true), refreshSeconds * 1000);
    }
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load, refreshSeconds]);

  return { ...state, reload: () => load(false) };
};

const MyDeskWidgetCard = ({
  widget,
  catalogEntry,
  editMode,
  isPlaceholder,
  isKbLifted,
  isResizing,
  onRemove,
  onGripKey,
  onGripBlur,
  onHeaderPointerDown,
  resizeApi,
  registerTitle,
  instructionsId,
}) => {
  // The header is the drag handle; the grid owns what happens during a drag.
  const { setNodeRef, listeners } = useDraggable({ id: widget.id, disabled: !editMode });
  const cardRef = useRef(null);
  const setRefs = useCallback((node) => {
    cardRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  const refreshSeconds = catalogEntry?.refresh_seconds ?? 60;
  const { data, loading, error, reload } = useWidgetData(widget.id, refreshSeconds);

  const Body = getWidgetBody(widget.type);
  const isPinned = typeof widget.type === 'string' && widget.type.startsWith('pinned_');
  const catalogTitle = catalogEntry?.title || widget.type;
  // Pinned cards show the object's own number as the title and the widget
  // type as a small kicker, so a board with three pinned invoices reads well.
  const title = isPinned && data?.title ? data.title : catalogTitle;
  const kicker = isPinned ? catalogTitle : null;
  const count = data && typeof data.total === 'number' ? data.total : null;
  const remove = () => onRemove(widget.id);

  useEffect(() => {
    if (registerTitle) registerTitle(widget.id, title);
  }, [registerTitle, widget.id, title]);

  // Edge handle: the card's width follows the pointer while the grid snaps to
  // whole columns underneath; on release it settles on the snapped width.
  const resizeRef = useRef(null);
  const handleResizeDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const node = cardRef.current;
    const metrics = node && resizeApi.start(widget.id);
    if (!metrics) return;
    event.preventDefault();
    event.stopPropagation();
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (err) { /* pointer already gone */ }
    resizeRef.current = { startX: event.clientX, startWidth: node.getBoundingClientRect().width, startSpan: metrics.span, span: metrics.span, metrics };
    node.style.transition = 'none';
  };
  const handleResizeMove = (event) => {
    const r = resizeRef.current;
    const node = cardRef.current;
    if (!r || !node) return;
    const { columnWidth, gap, columns, width: gridWidth } = r.metrics;
    const width = Math.max(columnWidth, Math.min(gridWidth, r.startWidth + event.clientX - r.startX));
    node.style.width = `${width}px`;
    const span = snapSpan(width, columnWidth, gap, columns);
    if (span !== r.span) {
      r.span = span;
      resizeApi.preview(widget.id, span, columns);
    }
  };
  const handleResizeEnd = (cancelled) => () => {
    const r = resizeRef.current;
    resizeRef.current = null;
    if (!r) return;
    const span = cancelled ? r.startSpan : r.span;
    const { columnWidth, gap, columns } = r.metrics;
    resizeApi.end(widget.id, span, columns);
    const node = cardRef.current;
    if (!node) return;
    const clear = () => {
      node.style.transition = '';
      node.style.width = '';
    };
    if (prefersReducedMotion()) {
      clear();
      return;
    }
    node.style.transition = 'width var(--motion-layout)';
    node.style.width = `${span * columnWidth + (span - 1) * gap}px`;
    setTimeout(clear, 300);
  };
  const handleResizeKey = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    resizeApi.step(widget.id, event.key === 'ArrowRight' ? 1 : -1);
  };

  // The grid starts following the pointer before dnd-kit sees the press.
  const headHandlers = editMode ? {
    ...listeners,
    onPointerDown: (event) => {
      if (onHeaderPointerDown) onHeaderPointerDown(event);
      if (listeners && listeners.onPointerDown) listeners.onPointerDown(event);
    },
  } : {};

  const cardClass = [
    'mydesk-card',
    editMode && 'is-editing',
    isPlaceholder && 'is-placeholder',
    isKbLifted && 'is-kb-lifted',
    isResizing && 'is-resizing',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={setRefs}
      className={cardClass}
      data-flip-id={widget.id}
      data-size={widget.size || 'third'}
      data-widget-type={widget.type}
    >
      <div className="mydesk-card-head" {...headHandlers}>
        {editMode && (
          <button
            type="button"
            className="mydesk-grip"
            aria-label={`Move ${title}`}
            aria-describedby={instructionsId}
            aria-pressed={Boolean(isKbLifted)}
            title="Drag to move, or press Space and use the arrow keys"
            onKeyDown={(event) => onGripKey(widget.id, event)}
            onBlur={() => onGripBlur(widget.id)}
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </button>
        )}
        <span className="mydesk-card-heading">
          {kicker && (
            <span className="mydesk-card-kicker">
              <FontAwesomeIcon icon={faThumbtack} />
              {kicker}
            </span>
          )}
          <span className="mydesk-card-title">{title}</span>
        </span>
        {count !== null && count > 0 && <span className="mydesk-count">{count}</span>}
        <span className="mydesk-head-spacer" />
        {editMode ? (
          <div className="mydesk-card-controls">
            <div className="mydesk-size-toggle" role="group" aria-label={`Width of ${title}`}>
              {SIZES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  title={s.title}
                  aria-pressed={(widget.size || 'third') === s.value}
                  className={(widget.size || 'third') === s.value ? 'is-on' : ''}
                  onClick={() => resizeApi.setSize(widget.id, s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="mydesk-icon-btn"
              title="Remove from My Workspace"
              aria-label={`Remove ${title}`}
              onClick={remove}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        ) : (
          error && (
            <button type="button" className="mydesk-icon-btn" title="Retry" aria-label="Retry" onClick={reload}>
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          )
        )}
      </div>

      <div className="mydesk-card-body">
        {loading && !data && <div className="mydesk-body-loading" aria-busy="true">Loading</div>}
        {error && !data && <div className="mydesk-body-error" role="alert">{error}</div>}
        {data && Body && <Body data={data} widgetId={widget.id} onRemove={remove} />}
        {data && !Body && <div className="mydesk-body-error">Unknown widget type.</div>}
      </div>

      {editMode && (
        <button
          type="button"
          className="mydesk-resize-handle"
          aria-label={`Resize ${title}`}
          title="Drag to change the width, or use the left and right arrow keys"
          onPointerDown={handleResizeDown}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd(false)}
          onPointerCancel={handleResizeEnd(true)}
          onKeyDown={handleResizeKey}
        />
      )}
    </div>
  );
};

// Memoised: while a card is dragged only the cards whose props change render
// again, so a reflow of a full board stays within a frame.
export default React.memo(MyDeskWidgetCard);
